"use client";

import { useEffect, useId, useState } from "react";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlayCircleIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import ProgressBar from "./ProgressBar";
import StepLink from "./StepLink";
import VisibilityToggle from "./VisibilityToggle";
import ContactCenti from "@/app/(procure)/procure/dashboard/_components/ContactCenti";
import { trackProductEvent } from "@/lib/analytics/product";
import {
  GET_STARTED_PATH,
  displayTitle,
  learnHref,
  nextStep,
} from "../constants";
import type { Activation } from "../types";

const STORAGE_KEY = "centi.getStartedWidget";
const CONTACT_STEP_KEY = "contact_support";

// Every step is one full-width target that goes straight to where it's done.
const ROW_CLASS =
  "group -mx-2 flex w-[calc(100%+1rem)] items-center gap-2.5 rounded-md px-2 py-2 transition hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-zinc-800";

const readOpen = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "closed";
  } catch {
    return true;
  }
};

const writeOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "open" : "closed");
  } catch {
    // Storage can be unavailable (private mode); the widget still works.
  }
};

/**
 * Floating Get Started checklist shown on every Procure page (bottom-left, clear of
 * the WhatsApp bubble) until the required steps are done or the customer hides it.
 */
export default function GetStartedWidget({
  activation,
  contactPhone,
}: {
  activation: Activation;
  /** Pre-fills the callback form opened from the "talk to the team" step. */
  contactPhone?: string | null;
}) {
  const pathname = usePathname();
  const panelId = useId();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setOpen(readOpen());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      writeOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const toggle = (next: boolean) => {
    setOpen(next);
    writeOpen(next);
  };

  // Render after mount so the remembered open/closed state doesn't flash,
  // and stay out of the way on the full Get Started page.
  if (!mounted || pathname?.startsWith(GET_STARTED_PATH)) return null;

  const { steps, requiredCompleted, requiredTotal } = activation;
  const next = nextStep(steps);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => toggle(true)}
        aria-expanded={false}
        aria-controls={panelId}
        className="fixed bottom-3 left-3 z-40 flex items-center gap-2 rounded-full bg-white py-1.5 pl-1.5 pr-3 text-sm shadow-lg ring-1 ring-zinc-950/10 transition hover:ring-zinc-950/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-zinc-900 dark:ring-white/15 md:bottom-5 md:left-5">
        <span className="flex size-7 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">
          <RocketLaunchIcon className="size-4" aria-hidden="true" />
        </span>
        <span className="font-semibold text-zinc-950 dark:text-white">
          Get started
        </span>
        <ProgressBar
          done={requiredCompleted}
          total={requiredTotal}
          className="w-20"
        />
      </button>
    );
  }

  return (
    <section
      id={panelId}
      aria-label="Get started checklist"
      className="fixed bottom-3 left-3 z-40 w-[min(20rem,calc(100vw-1.5rem))] rounded-xl bg-white p-4 shadow-xl ring-1 ring-zinc-950/10 dark:bg-zinc-900 dark:ring-white/15 md:bottom-5 md:left-5">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-zinc-950 dark:text-white">
          <RocketLaunchIcon
            className="size-4 text-blue-600 dark:text-blue-400"
            aria-hidden="true"
          />
          Get started
        </p>
        <button
          type="button"
          onClick={() => toggle(false)}
          aria-expanded={true}
          aria-controls={panelId}
          aria-label="Minimise checklist"
          className="-mr-1.5 rounded-md p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-zinc-800 dark:hover:text-white">
          <ChevronDownIcon className="size-4" aria-hidden="true" />
        </button>
      </div>

      <ProgressBar
        done={requiredCompleted}
        total={requiredTotal}
        className="mt-2"
      />

      <ol className="mt-2">
        {steps.map((step) => {
          const isNext = next?.key === step.key;
          const watch = learnHref(step.learnPlacement);
          const row = (
            <>
              <span
                aria-hidden="true"
                className={clsx(
                  "flex size-[18px] shrink-0 items-center justify-center rounded-full",
                  step.completed
                    ? "bg-blue-600 text-white dark:bg-blue-500"
                    : clsx(
                        "border-[1.5px]",
                        !step.required && "border-dashed",
                        isNext
                          ? "border-blue-600 dark:border-blue-400"
                          : "border-zinc-300 dark:border-zinc-600",
                      ),
                )}>
                {step.completed && <CheckIcon className="size-3" />}
              </span>
              <span
                className={clsx(
                  "min-w-0 flex-1 text-left text-sm",
                  step.completed
                    ? "text-zinc-400 line-through dark:text-zinc-500"
                    : isNext
                      ? "font-semibold text-zinc-950 dark:text-white"
                      : "text-zinc-700 dark:text-zinc-200",
                )}>
                <span className="sr-only">
                  {step.completed ? "Done: " : "To do: "}
                </span>
                {displayTitle(step.title)}{" "}
                {!step.required && (
                  <span className="ml-0.5 text-xs font-normal text-zinc-400">
                    optional
                  </span>
                )}
              </span>
              <ChevronRightIcon
                className="size-4 shrink-0 text-zinc-400 transition group-hover:translate-x-0.5 group-hover:text-blue-600"
                aria-hidden="true"
              />
            </>
          );

          return (
            <li key={step.key}>
              {step.key === CONTACT_STEP_KEY ? (
                // Opens the contact options in place instead of leaving the page.
                <ContactCenti
                  defaultPhone={contactPhone}
                  contactStepDone={step.completed}
                  renderTrigger={(openContact) => (
                    <button
                      type="button"
                      className={ROW_CLASS}
                      onClick={() => {
                        trackProductEvent("get_started_step_clicked", {
                          key: step.key,
                          action: "do",
                        });
                        openContact();
                      }}>
                      {row}
                    </button>
                  )}
                />
              ) : (
                <StepLink
                  href={step.href}
                  stepKey={step.key}
                  action="do"
                  // Get out of the way of the page the customer is sent to.
                  onNavigate={() => toggle(false)}
                  className={ROW_CLASS}>
                  {row}
                </StepLink>
              )}
              {isNext && watch !== step.href && (
                <StepLink
                  href={watch}
                  stepKey={step.key}
                  action="watch"
                  onNavigate={() => toggle(false)}
                  className="mb-1.5 ml-[2.375rem] inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">
                  <PlayCircleIcon className="size-4" aria-hidden="true" />
                  Watch how
                </StepLink>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-1 flex justify-end border-t border-zinc-950/5 pt-2 dark:border-white/10">
        <VisibilityToggle dismissed={false} compact className="-mr-2 text-xs" />
      </div>
    </section>
  );
}
